import { VORANK_BILD_MAX_ZEICHEN, VORANK_BILD_UPLOAD_MAX_BYTES } from '../../../services/vorank/vorankUnterschriftTypes';

/**
 * Hochgeladenes Unterschriftsbild aufbereiten (docs/vorankuendigung-unterschrift-plan.md
 * §6.1, Weg 3 am Rechner).
 *
 * Reine Funktionen auf RGBA-Pixeln (`{ breite, hoehe, daten }`) — testbar ohne
 * DOM. Nur `bereiteUnterschriftBildAuf` am Ende fasst Browser-APIs an
 * (createImageBitmap, Canvas) und bleibt dünn.
 *
 * Ablauf: EXIF-Drehung beachten → Fast-Weiß transparent (kein weißer Kasten
 * auf der Unterschriftslinie) → auf den Inhalt zuschneiden (4 px Rand) → auf
 * höchstens 600 × 200 px verkleinern → PNG, unter der Grenze der RPC.
 */

export interface RgbaBild {
    breite: number;
    hoehe: number;
    /** RGBA, Zeile für Zeile — Länge breite × hoehe × 4. */
    daten: Uint8ClampedArray;
}

export interface Rahmen {
    x: number;
    y: number;
    breite: number;
    hoehe: number;
}

/** Inhalt schmaler als das → keine Unterschrift (Fleck, Punkt, Rauschen). */
export const MIN_INHALT_BREITE = 40;
export const KEINE_UNTERSCHRIFT_TEXT = 'Auf dem Bild ist keine Unterschrift zu erkennen.';

/** Ab dieser Deckkraft zählt ein Pixel als Inhalt (Kantenglättung und Rauschen nicht). */
export const ALPHA_INHALT = 32;

/** Helligkeit 0…1 nach ITU-R BT.601. */
const helligkeit = (r: number, g: number, b: number): number => (0.299 * r + 0.587 * g + 0.114 * b) / 255;

/**
 * Wie hell ist hier „Papier“? Ein Handyfoto zeigt weißes Papier oft grau
 * (Schatten, Weißabgleich) — dann bliebe mit einer festen Schwelle das ganze
 * Blatt stehen. Maßstab ist deshalb das 90. Perzentil der Helligkeit aller
 * deckenden Pixel: die Tinte bedeckt nur einen kleinen Teil, der Rest ist
 * Papier. Bei einem sauberen Scan ist das 1,0 und die Schwelle gilt wie
 * angegeben. Untergrenze 0,5: ein fast schwarzes Bild wird nicht schöngerechnet.
 *
 * Ist schon ein Fünftel des Bildes durchsichtig (PNG mit freigestellter
 * Unterschrift), gibt es kein Papier — die deckenden Pixel sind dann Tinte,
 * und deren hellste dürfen nicht als „Papier“ verschwinden: Maßstab 1,0.
 */
export const papierHelligkeit = (bild: RgbaBild): number => {
    const stufen = new Uint32Array(256);
    let gesamt = 0;
    let durchsichtig = 0;
    const d = bild.daten;
    for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) {
            durchsichtig += 1;
            continue;
        }
        stufen[Math.round(helligkeit(d[i], d[i + 1], d[i + 2]) * 255)] += 1;
        gesamt += 1;
    }
    if (gesamt === 0 || durchsichtig >= (gesamt + durchsichtig) * 0.2) return 1;
    const ziel = Math.ceil(gesamt * 0.9);
    let summe = 0;
    for (let s = 0; s < 256; s++) {
        summe += stufen[s];
        if (summe >= ziel) return Math.max(0.5, s / 255);
    }
    return 1;
};

/**
 * Fast-Weiß → transparent: Pixel, deren Helligkeit (relativ zum Papier, s.
 * `papierHelligkeit`) über `schwelle` liegt, bekommen Alpha 0. Liefert eine
 * Kopie; das Original bleibt unverändert.
 */
export const weissZuTransparent = (bild: RgbaBild, schwelle = 0.9): RgbaBild => {
    const papier = papierHelligkeit(bild);
    const daten = new Uint8ClampedArray(bild.daten);
    for (let i = 0; i < daten.length; i += 4) {
        if (daten[i + 3] === 0) continue;
        if (helligkeit(daten[i], daten[i + 1], daten[i + 2]) / papier > schwelle) daten[i + 3] = 0;
    }
    return { breite: bild.breite, hoehe: bild.hoehe, daten };
};

/** Kleinstes Rechteck um alle Pixel mit Alpha ≥ `alphaMin`; null = nichts da. */
export const inhaltsRahmen = (bild: RgbaBild, alphaMin = ALPHA_INHALT): Rahmen | null => {
    let minX = bild.breite;
    let minY = bild.hoehe;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < bild.hoehe; y++) {
        const zeile = y * bild.breite * 4;
        for (let x = 0; x < bild.breite; x++) {
            if (bild.daten[zeile + x * 4 + 3] < alphaMin) continue;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }
    }
    if (maxX < 0) return null;
    return { x: minX, y: minY, breite: maxX - minX + 1, hoehe: maxY - minY + 1 };
};

/**
 * Schneidet auf `rahmen` zu, mit `randPx` transparentem Rand rundum — auch
 * dort, wo der Rahmen am Bildrand liegt (dann wird transparent aufgefüllt).
 */
export const zuschneiden = (bild: RgbaBild, rahmen: Rahmen, randPx = 4): RgbaBild => {
    const rand = Math.max(0, Math.floor(randPx));
    const breite = rahmen.breite + 2 * rand;
    const hoehe = rahmen.hoehe + 2 * rand;
    const daten = new Uint8ClampedArray(breite * hoehe * 4);
    for (let y = 0; y < rahmen.hoehe; y++) {
        const qy = rahmen.y + y;
        if (qy < 0 || qy >= bild.hoehe) continue;
        for (let x = 0; x < rahmen.breite; x++) {
            const qx = rahmen.x + x;
            if (qx < 0 || qx >= bild.breite) continue;
            const q = (qy * bild.breite + qx) * 4;
            const z = ((y + rand) * breite + x + rand) * 4;
            daten[z] = bild.daten[q];
            daten[z + 1] = bild.daten[q + 1];
            daten[z + 2] = bild.daten[q + 2];
            daten[z + 3] = bild.daten[q + 3];
        }
    }
    return { breite, hoehe, daten };
};

/** Größe, die in maxB × maxH passt — nur verkleinern, Seitenverhältnis bleibt, mindestens 1 px. */
export const zielGroesse = (breite: number, hoehe: number, maxB = 600, maxH = 200): { breite: number; hoehe: number } => {
    if (breite <= 0 || hoehe <= 0) return { breite: 1, hoehe: 1 };
    const faktor = Math.min(1, maxB / breite, maxH / hoehe);
    return {
        breite: Math.max(1, Math.round(breite * faktor)),
        hoehe: Math.max(1, Math.round(hoehe * faktor)),
    };
};

/** Klartext, wenn auf dem Bild keine Unterschrift zu erkennen ist (leer oder Inhalt < 40 px breit); sonst null. */
export const pruefeUnterschriftBild = (bild: RgbaBild): string | null => {
    const rahmen = inhaltsRahmen(bild);
    if (!rahmen || rahmen.breite < MIN_INHALT_BREITE) return KEINE_UNTERSCHRIFT_TEXT;
    return null;
};

/** Die gewählte Datei, soweit der Browser sie beschreibt. */
export interface BildDateiInfo {
    name: string;
    type: string;
    size: number;
}

const HEIC = /\.(heic|heif)$/i;
const PNG_JPG_NAME = /\.(png|jpe?g)$/i;

/**
 * Taugt die Datei, bevor sie gelesen wird? PNG oder JPG, höchstens 5 MB.
 * HEIC (iPhone-Fotos) können Browser außerhalb von Safari nicht lesen — dann
 * der Hinweis zum Umspeichern. Eine PDF ist ausdrücklich nicht gemeint (E5).
 */
export const pruefeBildDatei = (d: BildDateiInfo): string | null => {
    const typ = (d.type || '').toLowerCase();
    if (typ === 'image/heic' || typ === 'image/heif' || HEIC.test(d.name || '')) {
        return 'Fotos im HEIC-Format kann diese Seite nicht lesen — bitte als JPG oder PNG speichern und erneut wählen.';
    }
    const passt = typ === 'image/png' || typ === 'image/jpeg' || (!typ && PNG_JPG_NAME.test(d.name || ''));
    if (!passt) {
        return 'Bitte ein Bild der Unterschrift als PNG oder JPG wählen. Eine unterschriebene PDF schicken Sie bitte per E-Mail an den Absender.';
    }
    if (d.size <= 0) return 'Die Datei ist leer.';
    if (d.size > VORANK_BILD_UPLOAD_MAX_BYTES) {
        const mb = Math.round(VORANK_BILD_UPLOAD_MAX_BYTES / (1024 * 1024));
        return `Das Bild ist größer als ${mb} MB. Bitte ein kleineres Bild wählen.`;
    }
    return null;
};

// ─── Browser ────────────────────────────────────────────────────────────────

/** Längste Kante beim Einlesen — ein 12-MP-Foto braucht für eine Unterschrift nicht jedes Pixel. */
const EINLESE_KANTE = 1600;
/** Wie oft das PNG noch kleiner gerechnet wird, wenn es über der Grenze liegt. */
const VERKLEINERN_VERSUCHE = 5;

const leinwand = (breite: number, hoehe: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } => {
    const canvas = document.createElement('canvas');
    canvas.width = breite;
    canvas.height = hoehe;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Der Browser kann das Bild nicht bearbeiten. Bitte die Unterschrift mit der Maus zeichnen.');
    return { canvas, ctx };
};

/**
 * Datei → PNG-data-URL der Unterschrift, transparent, zugeschnitten, ≤ 600 × 200 px
 * und ≤ `VORANK_BILD_MAX_ZEICHEN`. Wirft `Error` mit Klartext für den Bauherrn.
 */
export const bereiteUnterschriftBildAuf = async (datei: File): Promise<string> => {
    const absage = pruefeBildDatei(datei);
    if (absage) throw new Error(absage);

    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(datei, { imageOrientation: 'from-image' });
    } catch {
        throw new Error('Das Bild ließ sich nicht lesen. Bitte eine PNG- oder JPG-Datei wählen.');
    }

    const einlesen = zielGroesse(bitmap.width, bitmap.height, EINLESE_KANTE, EINLESE_KANTE);
    const quelle = leinwand(einlesen.breite, einlesen.hoehe);
    quelle.ctx.drawImage(bitmap, 0, 0, einlesen.breite, einlesen.hoehe);
    bitmap.close();
    const roh = quelle.ctx.getImageData(0, 0, einlesen.breite, einlesen.hoehe);

    const freigestellt = weissZuTransparent({ breite: roh.width, hoehe: roh.height, daten: roh.data });
    const fehlt = pruefeUnterschriftBild(freigestellt);
    const rahmen = inhaltsRahmen(freigestellt);
    if (fehlt || !rahmen) throw new Error(fehlt || KEINE_UNTERSCHRIFT_TEXT);
    const zugeschnitten = zuschneiden(freigestellt, rahmen);

    const zwischen = leinwand(zugeschnitten.breite, zugeschnitten.hoehe);
    zwischen.ctx.putImageData(new ImageData(zugeschnitten.daten, zugeschnitten.breite, zugeschnitten.hoehe), 0, 0);

    let ziel = zielGroesse(zugeschnitten.breite, zugeschnitten.hoehe);
    for (let versuch = 0; versuch <= VERKLEINERN_VERSUCHE; versuch++) {
        const aus = leinwand(ziel.breite, ziel.hoehe);
        aus.ctx.imageSmoothingEnabled = true;
        aus.ctx.drawImage(zwischen.canvas, 0, 0, ziel.breite, ziel.hoehe);
        const url = aus.canvas.toDataURL('image/png');
        if (url.length <= VORANK_BILD_MAX_ZEICHEN) return url;
        ziel = zielGroesse(ziel.breite, ziel.hoehe, Math.round(ziel.breite * 0.75), Math.round(ziel.hoehe * 0.75));
    }
    throw new Error('Das Bild bleibt auch verkleinert zu groß. Bitte ein schlichteres Foto (nur die Unterschrift auf weißem Papier) wählen oder mit der Maus unterschreiben.');
};
